/**
 * squareDashboardUtils.js — Domain constants, helpers, and date range logic
 * for the Square Sales Dashboard.
 */

import { tzDate, toLocalInput, toCST_ISO } from '../../utils/cstUtils';
import { isOnlineTicketName } from '../../utils/onlineTicketName';

// ─── API ──────────────────────────────────────────────────────────────────────
export const API_BASE =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001'
    : 'https://my-app-ramakrishna5s-projects.vercel.app';

// ─── Ticket-type detection ────────────────────────────────────────────────────
const CATERING_KEYWORDS = ['catering', 'cater'];

export function getTicketType(tkt) {
  const name = (tkt.ticketName || '').toLowerCase();
  if (CATERING_KEYWORDS.some(k => name.includes(k))) return 'catering';
  if (isOnlineTicketName(name))                       return 'online';
  if (tkt.status !== 'OPEN') return 'done';
  if ((tkt.discountCents || 0) > 0) return 'discounted';
  return 'misc';
}

export const TICKET_TYPE_META = {
  done:       { label: '\u2705 Done',             cls: 'done'       },
  online:     { label: '\uD83C\uDF10 Online',      cls: 'online'     },
  catering:   { label: '\uD83C\uDF74 Catering',    cls: 'catering'   },
  discounted: { label: '\uD83C\uDFF7\uFE0F Disc.',  cls: 'discounted' },
  misc:       { label: '\uD83D\uDCCB Misc',         cls: 'misc'       },
};

// ─── Date-range presets ───────────────────────────────────────────────────────
export const QUICK_PRESETS = [
  { key: 'm5',  label: '5m',   mins: 5   },
  { key: 'm10', label: '10m',  mins: 10  },
  { key: 'm15', label: '15m',  mins: 15  },
  { key: 'm20', label: '20m',  mins: 20  },
  { key: 'm30', label: '30m',  mins: 30  },
  { key: 'm40', label: '40m',  mins: 40  },
  { key: 'm50', label: '50m',  mins: 50  },
  { key: 'm60', label: '60m',  mins: 60  },
  { key: 'h2',  label: '2hr',  mins: 120 },
  { key: 'h3',  label: '3hr',  mins: 180 },
  { key: 'h4',  label: '4hr',  mins: 240 },
  { key: 'h5',  label: '5hr',  mins: 300 },
  { key: 'h8',  label: '8hr',  mins: 480 },
  { key: 'h10', label: '10hr', mins: 600 },
  { key: 'h12', label: '12hr', mins: 720 },
];

export const PRESETS = [
  { key: 'today',     label: 'Today'        },
  { key: 'yesterday', label: 'Yesterday'    },
  { key: '7d',        label: 'Last 7 Days'  },
  { key: '30d',       label: 'Last 30 Days' },
  { key: 'month',     label: 'This Month'   },
  { key: 'custom',    label: 'Custom'       },
];

/** Compute a { start, end } Date pair for a given preset key. */
export function computeRange(preset) {
  const now = new Date();
  // Quick rolling-window presets
  const quick = QUICK_PRESETS.find(q => q.key === preset);
  if (quick) {
    return { start: new Date(now.getTime() - quick.mins * 60 * 1000), end: now };
  }
  const ts = tzDate(now);
  const te = new Date(ts);
  te.setHours(23, 59, 59, 999);
  if (preset === 'today')     return { start: ts, end: te };
  if (preset === 'yesterday') {
    const s = new Date(ts); s.setDate(s.getDate() - 1);
    const e = new Date(s);  e.setHours(23, 59, 59, 999);
    return { start: s, end: e };
  }
  if (preset === '7d')  { const s = new Date(ts); s.setDate(s.getDate() - 6);  return { start: s, end: te }; }
  if (preset === '30d') { const s = new Date(ts); s.setDate(s.getDate() - 29); return { start: s, end: te }; }
  if (preset === 'month') {
    const s = tzDate(now); s.setDate(1);
    return { start: s, end: te };
  }
  return { start: ts, end: te };
}

/** True when the preset is a quick rolling-window (minutes/hours) key. */
export function isQuickPreset(preset) {
  return QUICK_PRESETS.some(q => q.key === preset);
}

// ─── Column definitions (module-level = stable reference in dep arrays) ───────
export const TKT_COL_DEFS = [
  { key: 'ticketName',    type: 'namefilter' },
  { key: 'shortId',       type: 'text'   },
  { key: 'itemQty',       type: 'min'    },
  { key: 'revenueCents',  type: 'minmax' },
  { key: 'discountCents', type: 'min'    },
];

export const ALL_COL_DEFS = [
  { key: 'name',          type: 'text'   },
  { key: 'catStr',        type: 'text'   },
  { key: 'qty',           type: 'min'    },
  { key: 'revenueCents',  type: 'minmax' },
  { key: 'discountCents', type: 'min'    },
  { key: 'returnQty',     type: 'min'    },
];

/**
 * Build a human-readable range label (e.g. "Last 20min · 2:10 – 2:30 PM CST")
 * @param {string} preset
 * @param {Date}   startDt
 * @param {Date}   endDt
 * @param {string} TZ - IANA timezone string
 */
export function buildRangeLabel(preset, startDt, endDt, TZ) {
  const quick = QUICK_PRESETS.find(q => q.key === preset);
  if (quick) {
    const now   = new Date();
    const start = new Date(now.getTime() - quick.mins * 60 * 1000);
    const tf    = d => d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true });
    const df    = d => d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric' });
    const sameDay =
      start.toLocaleDateString('en-US', { timeZone: TZ }) ===
      now.toLocaleDateString('en-US', { timeZone: TZ });
    const rangeStr = quick.mins >= 60 ? `${quick.mins / 60}hr` : `${quick.mins}min`;
    return `Last ${rangeStr} \u00B7 ${sameDay ? tf(start) : df(start) + ' ' + tf(start)} \u2013 ${tf(now)} CST`;
  }
  const o = { month: 'short', day: 'numeric', timeZone: TZ };
  return `${startDt.toLocaleDateString('en-US', o)} \u2013 ${endDt.toLocaleDateString('en-US', o)} CST`;
}

/** Helper used by TopBar to initialise custom inputs when switching presets. */
export { toLocalInput };

// ─── Shared date helpers (used by AITab, CompareTab, WeatherTab, GrowthTab) ──

/** Format a Date as YYYY-MM-DD in local time. */
export function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Format a timestamp (ms) as a short time string like "2:30 PM". */
export function fmtTime(ms) {
  return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** How many minutes ago was this timestamp? */
export function minutesAgo(ms) {
  return Math.round((Date.now() - ms) / 60000);
}

// ─── WMO weather code decoder ────────────────────────────────────────────────

export function decodeWMO(code) {
  if (code === 0)                   return { label: 'Clear Sky',           emoji: '☀️',  cat: 'clear'  };
  if (code === 1)                   return { label: 'Mainly Clear',        emoji: '🌤️', cat: 'clear'  };
  if (code === 2)                   return { label: 'Partly Cloudy',       emoji: '⛅',  cat: 'cloudy' };
  if (code === 3)                   return { label: 'Overcast',            emoji: '☁️',  cat: 'cloudy' };
  if ([45, 48].includes(code))      return { label: 'Foggy',               emoji: '🌫️', cat: 'foggy'  };
  if ([51, 53, 55].includes(code))  return { label: 'Drizzle',             emoji: '🌦️', cat: 'rainy'  };
  if ([56, 57].includes(code))      return { label: 'Freezing Drizzle',    emoji: '🌨️', cat: 'snowy'  };
  if ([61, 63, 65].includes(code))  return { label: 'Rain',                emoji: '🌧️', cat: 'rainy'  };
  if ([66, 67].includes(code))      return { label: 'Freezing Rain',       emoji: '🌨️', cat: 'snowy'  };
  if ([71, 73, 75, 77].includes(code)) return { label: 'Snow',             emoji: '❄️',  cat: 'snowy'  };
  if ([80, 81, 82].includes(code))  return { label: 'Rain Showers',        emoji: '🌧️', cat: 'rainy'  };
  if ([85, 86].includes(code))      return { label: 'Snow Showers',        emoji: '🌨️', cat: 'snowy'  };
  if (code === 95)                  return { label: 'Thunderstorm',        emoji: '⛈️', cat: 'stormy' };
  if ([96, 99].includes(code))      return { label: 'Severe Thunderstorm', emoji: '🌩️', cat: 'stormy' };
  return { label: 'Unknown', emoji: '🌡️', cat: 'cloudy' };
}

// ─── Shared API fetchers ─────────────────────────────────────────────────────

export async function fetchLiveTickets() {
  const res = await fetch(`${API_BASE}/api/square-tickets`);
  if (!res.ok) throw new Error(`Tickets API ${res.status}`);
  return res.json();
}

export async function fetchTodaySales() {
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const url = `${API_BASE}/api/square-sales` +
    `?startAt=${encodeURIComponent(toCST_ISO(start))}` +
    `&endAt=${encodeURIComponent(toCST_ISO(new Date()))}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Sales API ${res.status}`);
  return res.json();
}

export async function fetchDaySales(dateStr) {
  const start = new Date(`${dateStr}T00:00:00`);
  const end   = new Date(`${dateStr}T23:59:59`);
  const url   = `${API_BASE}/api/square-sales` +
    `?startAt=${encodeURIComponent(toCST_ISO(start))}` +
    `&endAt=${encodeURIComponent(toCST_ISO(end))}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = await res.json();
    const rev = parseFloat(json?.summary?.totalRevenue || 0);
    return rev > 0 ? rev : null;
  } catch { return null; }
}

export async function fetchHistoricalSales(days = 60) {
  const today = tzDate(new Date());
  const results = {};
  const dates = [];
  for (let i = 1; i <= days; i++) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    dates.push(isoDate(d));
  }
  for (let i = 0; i < dates.length; i += 14) {
    const chunk = dates.slice(i, i + 14);
    const vals = await Promise.all(chunk.map(ds => fetchDaySales(ds)));
    chunk.forEach((d, idx) => { results[d] = vals[idx]; });
  }
  return results;
}

export async function fetchCurrentWeather() {
  const url = `https://api.open-meteo.com/v1/forecast` +
    `?latitude=36.37&longitude=-94.21` +
    `&current=temperature_2m,weather_code,wind_speed_10m,precipitation` +
    `&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch` +
    `&timezone=America%2FChicago`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = await res.json();
    const cur = json.current;
    if (!cur) return null;
    return { temp: cur.temperature_2m, wind: cur.wind_speed_10m, precip: cur.precipitation, wmo: cur.weather_code };
  } catch { return null; }
}
